import { isDefined } from "@clipboard-health/util-ts";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { berlinTheme } from "@src/appV2/redesign/theme/theme";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import { type WorkerShift } from "./api/useGetWorkerShifts";
import { type ShiftSearchResult } from "./api/useSearchShift";
import { getShortcutHint, KeyboardHints } from "./components/KeyboardHints";
import { type ShiftAction } from "./components/ShiftActionItem";
import { useOmnisearchKeyboard } from "./hooks/useOmnisearchKeyboard";
import { type SearchResultItem, useOmnisearchResults } from "./hooks/useOmnisearchResults";
import { OmnisearchResultsList } from "./OmnisearchResultsList";

interface OmnisearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectAgent: (agent: Agent) => void;
  onSelectFacility: (facility: Facility) => void;
  onSelectShift: (shift: ShiftSearchResult) => void;
  onSelectShiftAction: (shift: ShiftSearchResult, action: ShiftAction) => void;
  onSelectAgentShift: (workerShift: WorkerShift) => void;
}

export function OmnisearchDialog(props: OmnisearchDialogProps) {
  const {
    open,
    onClose,
    onSelectAgent,
    onSelectFacility,
    onSelectShift,
    onSelectShiftAction,
    onSelectAgentShift,
  } = props;
  const [searchInput, setSearchInput] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedShiftId, setExpandedShiftId] = useState<string | null>(null);
  const [expandedWorkerId, setExpandedWorkerId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousShiftIdRef = useRef<string | null>(null);

  const {
    agents,
    facilities,
    shift,
    shiftData,
    workerShifts,
    allResults,
    isLoading,
    isLoadingWorkerShifts,
    workerHasShifts,
    hasResults,
    hasSearchInput,
    debouncedSearchInput,
  } = useOmnisearchResults({ searchInput, expandedShiftId, expandedWorkerId });

  const handleSelect = useCallback(
    (item: SearchResultItem) => {
      switch (item.type) {
        case "agent": {
          onSelectAgent(item.data);
          break;
        }
        case "facility": {
          onSelectFacility(item.data);
          break;
        }
        case "shift": {
          onSelectShift(item.data);
          break;
        }
        case "shift-action": {
          onSelectShiftAction(item.data, item.action);
          break;
        }
        case "agent-shift": {
          onSelectAgentShift(item.data);
          break;
        }
        default: {
          break;
        }
      }
      onClose();
      setSearchInput("");
      setSelectedIndex(0);
      setExpandedShiftId(null);
      setExpandedWorkerId(null);
      previousShiftIdRef.current = null;
    },
    [
      onSelectAgent,
      onSelectFacility,
      onSelectShift,
      onSelectShiftAction,
      onSelectAgentShift,
      onClose,
    ]
  );

  const { handleKeyDown, copiedItem } = useOmnisearchKeyboard({
    allResults,
    selectedIndex,
    setSelectedIndex,
    setExpandedShiftId,
    setExpandedWorkerId,
    onSelect: handleSelect,
  });

  useEffect(() => {
    setSelectedIndex(0);
    setExpandedWorkerId(null);
  }, [debouncedSearchInput]);

  useEffect(() => {
    if (isDefined(shiftData) && shiftData.id !== previousShiftIdRef.current) {
      previousShiftIdRef.current = shiftData.id;
      setExpandedShiftId(shiftData.id);
    }
  }, [shiftData]);

  const handleClose = useCallback(() => {
    onClose();
    setSearchInput("");
    setSelectedIndex(0);
    setExpandedShiftId(null);
    setExpandedWorkerId(null);
    previousShiftIdRef.current = null;
  }, [onClose]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  return (
    <ThemeProvider theme={berlinTheme}>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        PaperProps={{
          sx: { position: "fixed", top: "15%", margin: "0", borderRadius: 2, maxHeight: "70vh" },
        }}
        onClose={handleClose}
      >
        <DialogContent sx={{ p: "8px" }}>
          <TextField
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            fullWidth
            inputRef={inputRef}
            placeholder="Search professionals, workplaces..."
            value={searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Text
                    sx={{
                      color: "text.secondary",
                      backgroundColor: "action.hover",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontFamily: "monospace",
                    }}
                    variant="caption"
                  >
                    {getShortcutHint()}
                  </Text>
                </InputAdornment>
              ),
              sx: { py: 1.5, px: 2, "& fieldset": { border: "none" } },
            }}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />

          {hasSearchInput && (
            <Box sx={{ borderTop: 1, borderColor: "divider" }}>
              {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!isLoading && !hasResults && (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Text color="text.secondary">
                    No results found for &quot;{debouncedSearchInput}&quot;
                  </Text>
                </Box>
              )}
              {!isLoading && hasResults && (
                <>
                  <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                    <OmnisearchResultsList
                      agents={agents}
                      facilities={facilities}
                      shift={shift}
                      expandedShiftId={expandedShiftId}
                      expandedWorkerId={expandedWorkerId}
                      workerShifts={workerShifts}
                      isLoadingWorkerShifts={isLoadingWorkerShifts}
                      workerHasShifts={workerHasShifts}
                      selectedIndex={selectedIndex}
                      copiedItem={copiedItem}
                      onSelect={handleSelect}
                    />
                  </Box>
                  <Box
                    sx={(theme) => ({
                      paddingTop: 2,
                      paddingBottom: 1.5,
                      paddingX: 2,
                      borderTop: 1,
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      "& > *:not(:last-child)": {
                        borderRight: 1,
                        borderColor: theme.palette.divider,
                        paddingRight: 2,
                      },
                    })}
                  >
                    <KeyboardHints />
                  </Box>
                </>
              )}
            </Box>
          )}

          {!hasSearchInput && (
            <Box sx={{ py: 3, px: 2, borderTop: 1, borderColor: "divider" }}>
              <Text color="text.secondary" sx={{ textAlign: "center" }} variant="body2">
                Start typing to search for professionals or workplaces
              </Text>
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
                <KeyboardHints />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
